import { Container } from '@mui/material';
import type { ErrorResponse } from '@remix-run/cloudflare';
import { isRouteErrorResponse, useRouteError } from '@remix-run/react';
import { GlobalNavigationBar } from '#app/components/GlobalNavigationBar.js';
import { useOptionalUser } from '#app/hooks/useUser.js';
import { createLogger } from '#app/logging.js';

export function AuthenticatedLayout({
  children,
  canAccessDashboard = false,
  disableMaxWidth,
}: {
  children: React.ReactNode;
  canAccessDashboard?: boolean;
  disableMaxWidth?: boolean;
}) {
  const maybeUser = useOptionalUser();
  const maxWidth = disableMaxWidth ? false : 'xxl';
  return (
    <>
      <GlobalNavigationBar
        user={maybeUser}
        canAccessDashboard={canAccessDashboard}
      />
      <Container maxWidth={maxWidth} component="main">
        {children}
      </Container>
    </>
  );
}

export default AuthenticatedLayout;

export function ErrorBoundary() {
  const error = useRouteError();
  const logger = createLogger('authenticated-error-boundary');
  // this is what used to go to `CatchBoundary`
  if (isRouteErrorResponse(error)) {
    return handleErrorResponse(error);
  } else if (error instanceof Error) {
    logger.error(error);
    return handleError(error);
  }
  logger.debug('unknown error', error);
  return handleError();
}

function handleError(error?: Error) {
  return (
    <AuthenticatedLayout>
      <div>
        <h1>An unexpected error occurred</h1>
        <p>
          It may work if you <a href="">try again</a>. Otherwise, go back to the{' '}
          <a href="/">home page</a>
        </p>
        {error?.stack ? (
          <>
            <hr />
            <small>
              <details>
                <summary>error details</summary>
                <pre>{error.stack}</pre>
              </details>
            </small>
          </>
        ) : null}
      </div>
    </AuthenticatedLayout>
  );
}

function handleErrorResponse(response: ErrorResponse) {
  let message;
  switch (response.status) {
    case 401:
      message = (
        <p>
          Oops! Looks like you are not logged in.{' '}
          <a href="/login">Try logging in again</a>
        </p>
      );
      break;
    case 404:
      message = (
        <>
          <p>Oops! Looks like you tried to visit a page that does not exist.</p>
          <p>
            Go to the <a href="/">home page</a>
          </p>
        </>
      );
      break;

    default:
      throw new Error(response.data || response.statusText);
  }

  return (
    <AuthenticatedLayout>
      <h1>
        {response.status}: {response.statusText}
      </h1>
      {message}
    </AuthenticatedLayout>
  );
}
